import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { sharedLib } from "@shared/services/shared.service";
import {
  AmountType,
  AnalyticsPageName,
  BacktestingRunType,
  BotType,
  DisplayResultMessage,
  LanguageNameSpaces,
  StartBacktestingRunRequest,
  UsageType,
} from "@shared/types";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  makeNameUnique,
  updateGetBacktestingRuns,
  updateGetBotsNames,
} from "@shared/features/bots/utils";
import { useDispatch } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs, { Dayjs } from "dayjs";
import { isSymbol } from "@shared/types/utils";
import { MAX_BACKTEST_NAME_LENGTH } from "@shared/features/bots/BotForm/common/constants";
import { dateToDayjs } from "@shared/utils/date";
import moment from "moment";
import { Error } from "@shared/features/bots/BotForm/common/types";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import usePlanUsage from "@shared/features/settings/subscription/hooks/usePlanUsage";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { useGetBacktestingRunsArgs } from "@shared/features/bots/BotForm/Backtesting/hooks";
import useGroupMainCoinsSignalsCount from "@shared/features/bots/BotForm/useGroupMainCoinsSignalsCount";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface useBacktestModalProps {
  closeModal: () => void;
  displayResultMessage: DisplayResultMessage;
  onStartBacktesting?: (response: BacktestingRunType) => void;
  isMobileApp?: boolean;
}

const useBacktestModal = ({
  closeModal,
  displayResultMessage,
  onStartBacktesting,
  isMobileApp,
}: useBacktestModalProps) => {
  const dispatch = useDispatch();
  const {
    getParsedFormData,
    name,
    account,
    subGroup,
    errors: botErrors,
    getBotAmountDataStatus,
    botAmountData,
    amountType,
    botType,
    symbols,
  } = useContext(BotFormContext)!;
  const { userUsageLimits, allQuotaUsage } = usePlanUsage();
  const { getMainCoin } = useSymbolsMetadata();
  const botFormArgs = useGetBacktestingRunsArgs();
  const seeAllPageArgs = useMemo(
    () => (botType ? { bot_type: botType } : skipToken),
    [botType]
  );
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);

  const [botName, setBotName] = useState<string>();
  const [timeframe, setTimeframe] = useState<{
    start: number | null;
    end: number | null;
  }>();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [destroyOnClose, setDestroyOnClose] = useState(false);
  const [mainCoinsAmount, setMainCoinsAmount] =
    useState<Record<string, number | null>>();
  const [freeTrialModalOpen, setFreeTrialModalOpen] = useState(false);
  const accountId = !isSymbol(account) ? account?.client_id : undefined;
  const exchange = !isSymbol(account) ? account?.exchange : undefined;
  const isSignalsBot = botType === BotType.SIGNAL;
  const isDcaBot = botType === BotType.ASAP;

  const backtestingLimit = userUsageLimits?.[UsageType.BACKTESTING];
  const backtestingUsage = allQuotaUsage?.[UsageType.BACKTESTING];
  const { data: minBacktestTimestampData } =
    sharedLib.sharedApi.bots.useGetMinimumBacktestingTimestampQuery(
      exchange ? { exchange } : skipToken
    );

  const { mainCoinsToSignalsCount, isLoading: mainCoinsLoading } =
    useGroupMainCoinsSignalsCount({ isBacktestModal: true, timeframe });

  const mainCoinsAvailableAmountData = useMemo(() => {
    if (!accountId) return undefined;
    if (isSignalsBot) {
      return getBotAmountDataStatus.data?.data?.main_coin?.available?.[
        accountId
      ];
    } else if (isDcaBot) {
      return getBotAmountDataStatus.data?.data?.[accountId];
    }
  }, [accountId, botType, getBotAmountDataStatus]);

  const getBotsNamesArgs = {
    bot_type: botType,
    is_backtesting: true,
  };
  const { data: botsNames } =
    sharedLib.sharedApi.bots.useGetBotsNamesQuery(getBotsNamesArgs);
  const [startBacktestingRun, { isLoading: startBacktestingLoading }] =
    sharedLib.sharedApi.bots.useStartBacktestingRunMutation();

  const botMainCoin = useMemo(
    () => (!isSymbol(symbols) ? getMainCoin(symbols[0], exchange) : undefined),
    [symbols, exchange]
  );

  const mainCoins = useMemo(() => {
    if (isSignalsBot && exchange && mainCoinsToSignalsCount?.[exchange]) {
      return Object.entries(mainCoinsToSignalsCount?.[exchange]);
    } else if (isDcaBot && botMainCoin) {
      return [[botMainCoin, 0]] as [string, number][];
    }
    return [];
  }, [mainCoinsToSignalsCount, exchange, botType, botMainCoin]);

  useEffect(() => {
    if (name && !isSymbol(name)) {
      setBotName(makeNameUnique(name, botsNames));
    }
  }, [name, botsNames]);

  useEffect(() => {
    setTimeframe(undefined);
    setMainCoinsAmount(undefined);
  }, [subGroup, account]);

  useEffect(() => {
    if (mainCoins.length && getBotAmountDataStatus.isSuccess) {
      let mainCoinsAmount = {};
      mainCoins.forEach(([coin]) => {
        mainCoinsAmount = {
          ...mainCoinsAmount,
          [coin]: mainCoinsAvailableAmountData?.[coin]?.amount || 0,
        };
      });
      setMainCoinsAmount(mainCoinsAmount);
    }
  }, [
    mainCoins,
    mainCoinsAvailableAmountData,
    getBotAmountDataStatus.isSuccess,
    timeframe,
  ]);

  const getBotNameError = () => {
    const botsNamesArray = Object.values(botsNames || {})?.map((botName) =>
      botName.toLowerCase()
    );
    if (!botName) return t("backtesting.modal.errors.choose_backtesting_name");
    if (botName.length > MAX_BACKTEST_NAME_LENGTH)
      return t("backtesting.modal.errors.backtesting_name_too_long", {
        max: MAX_BACKTEST_NAME_LENGTH,
      });
    if (botsNamesArray.includes(botName?.toLowerCase() || ""))
      return t("backtesting.modal.errors.backtesting_name_exists");
  };

  const getTimeframeError = () => {
    if (!timeframe?.start || !timeframe.end) {
      return t("backtesting.modal.errors.choose_timeframe");
    }
  };

  const getInitialFundsError = () => {
    const noCoinWithAmount = !Object.values(mainCoinsAmount || {}).some(
      (coinAmount) => coinAmount && coinAmount > 0
    );
    if (noCoinWithAmount) {
      return isSignalsBot
        ? t("backtesting.modal.errors.at_least_one_coin")
        : t("backtesting.modal.errors.value_greater_than_zero");
    }
  };

  const handleUpdateBacktestingRuns = async (
    newBacktestingRun?: BacktestingRunType
  ) => {
    if (!newBacktestingRun) return;
    if (!isSymbol(botFormArgs)) {
      //@ts-ignore
      dispatch(updateGetBacktestingRuns(newBacktestingRun, botFormArgs));
    }
    if (!isSymbol(seeAllPageArgs)) {
      //@ts-ignore
      dispatch(updateGetBacktestingRuns(newBacktestingRun, seeAllPageArgs));
    }
  };

  const handleUpdateBotsNames = (botId: number, botName: string) => {
    //@ts-ignore
    dispatch(updateGetBotsNames(botId, botName, getBotsNamesArgs));
  };

  const onTimeframeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (destroyOnClose) setDestroyOnClose(false);
    if (dates && (dates[0] || dates[1])) {
      setTimeframe({
        start: dates[0]?.startOf("day").unix() ?? null,
        end: dates[1]?.endOf("day").unix() ?? null,
      });
    } else {
      setTimeframe(undefined);
    }
  };

  const getRangePickerValue = (): [Dayjs | null, Dayjs | null] | undefined => {
    if (!timeframe) return;
    const { start, end } = timeframe;
    if (start || end)
      return [start ? dateToDayjs(start) : null, end ? dateToDayjs(end) : null];
  };

  const onCloseModal = () => {
    closeModal();
    if (freeTrialModalOpen) setFreeTrialModalOpen(false);
    setDestroyOnClose(true);
    setTimeframe(undefined);
  };

  const filteredBotErrors = useMemo(
    () => botErrors.filter((error) => !IGNORE_ERRORS.includes(error)),
    [botErrors]
  );
  const startButtonDisabled =
    !!getBotNameError() ||
    !!getTimeframeError() ||
    !!getInitialFundsError() ||
    filteredBotErrors.length > 0 ||
    !mainCoins?.length;

  const botErrorMessage =
    botErrors.length > 0 && t("backtesting.modal.errors.resolve_bot_errors");

  const handleStartBacktesting = () => {
    setShowErrorMessage(false);
    const botData = getParsedFormData(IGNORE_ERRORS);
    const finalTimeframe = timeframe ? { ...timeframe } : undefined;
    const currentUnix = moment().unix();
    if (
      finalTimeframe &&
      finalTimeframe.end &&
      finalTimeframe.end > currentUnix
    ) {
      finalTimeframe.end = currentUnix;
    }
    const args =
      botData && botName && finalTimeframe
        ? {
            ...botData,
            is_backtesting: true,
            dca_bot_name: botName,
            backtesting_time_range: finalTimeframe,
            backtesting_main_coins_amounts: mainCoinsAmount,
          }
        : undefined;
    if (!args) return;
    displayResultMessage(
      startBacktestingRun(args as StartBacktestingRunRequest).unwrap()
    )
      .then((response) => {
        handleUpdateBacktestingRuns(response).then(() => {
          onCloseModal();
          setTimeout(() => {
            handleUpdateBotsNames(response.bot_id, response.name);
          }, 1000);
          onStartBacktesting?.(response);
        });
      })
      .catch(() => {});
  };

  const onStart = () => {
    if (startButtonDisabled) {
      setShowErrorMessage(true);
    } else if (subGroup?.isFreeTrialGroup) {
      closeModal();
      setTimeout(() => setFreeTrialModalOpen(true), isMobileApp ? 500 : 0);
    } else {
      handleStartBacktesting();
    }
  };

  const nameData = {
    title: t("backtesting.modal.backtesting_name"),
    value: botName,
    onChange: setBotName,
    error: getBotNameError(),
  };

  const timeframeData = {
    title: t("backtesting.modal.timeframe"),
    value: getRangePickerValue(),
    onChange: onTimeframeChange,
    error: showErrorMessage && getTimeframeError(),
    minValue: dateToDayjs(minBacktestTimestampData?.minimum_timestamp).startOf(
      "day"
    ),
    maxValue: dayjs().endOf("day"),
  };

  const initialFundsData = {
    show: !!(timeframe?.start && timeframe?.end),
    title: t("backtesting.modal.initial_funds"),
    loading: mainCoinsLoading,
    data: mainCoins,
    showEmpty: !mainCoins?.length,
    emptyText: t("backtesting.modal.empty.no_signals_in_timeframe"),
    getValue: (coin: string) => mainCoinsAmount?.[coin],
    onChange: (coin: string, value: number | null) =>
      setMainCoinsAmount((prev) => ({
        ...prev,
        [coin]: value,
      })),
    error: getInitialFundsError(),
    helperProps: {
      description: t("backtesting.modal.helper_tooltips.initial_funds"),
      analyticsFieldName: "Initial Funds",
      analyticsPageName: AnalyticsPageName.BACKTESTING,
      articleId: "9576302",
    },
  };

  const getEquivalentAmount = () => {
    if (amountType === AmountType.FIXED_BTC_AMOUNT && botAmountData) {
      const usdAmount =
        botAmountData.convertMainCoinAmountToDisplayCurrency(
          botAmountData.mainCoinAmount,
          DISPLAY_CURRENCIES.USD,
          true
        ) || 0;
      return t("backtesting.modal.equivalent_to_usd", { amount: usdAmount });
    }
  };

  const footerData = {
    okButton: subGroup?.isFreeTrialGroup
      ? t("backtesting.modal.continue")
      : t("backtesting.modal.start"),
    onOk: onStart,
    okLoading: startBacktestingLoading,
    okDisabled: startButtonDisabled,
    cancelButton: t("backtesting.modal.cancel"),
    onCancel: onCloseModal,
    showError: showErrorMessage,
    error: botErrorMessage,
    backtestingLimit,
    backtestingUsage,
  };

  const backtestHelperProps = {
    description: t("backtesting.modal.helper_tooltips.backtest"),
    analyticsFieldName: "Backtest",
    analyticsPageName: AnalyticsPageName.BACKTESTING,
    articleId: "9576302",
  };

  const freeTrialModalData = {
    isOpen: freeTrialModalOpen,
    onCancel: () => setFreeTrialModalOpen(false),
    saveChanges: handleStartBacktesting,
  };

  return {
    nameData,
    timeframeData,
    initialFundsData,
    footerData,
    destroyOnClose,
    backtestHelperProps,
    equivalentAmount: getEquivalentAmount(),
    freeTrialModalData,
    isSignalsBot,
  };
};

export default useBacktestModal;

const IGNORE_ERRORS = [Error.BOT_NAME, Error.INSUFFICIENT_AMOUNT];
